import { FC, useEffect, useState } from "react";
import { TextField, Tooltip } from "@mui/material";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { GlobalButton, GlobalSecondaryButton } from "../../styles";
import { ProgressSpinner } from "../../../components/ProgressSpinner";
import SupplierAutocompleteField from "./SupplierAutocompleteField";
import TradeNameAutocompleteField from "./TradeNameAutocompleteField";
import {
  Supplier,
  SupplierTradeNameFormValues,
  SupplierTradenameAssociation,
  TradeName,
} from "../../../api/types";

interface SupplierTradeNameFormProps {
  suppliers?: Supplier[];
  tradenames?: TradeName[];
  existingAssociations?: SupplierTradenameAssociation[];
  selectedSupplier?: Supplier | null;
  selectedTradename?: TradeName | null;
  isSaving?: boolean;
  isLoading?: boolean;
  onSave?: (values: SupplierTradeNameFormValues) => void | Promise<void>;
  onCancel?: () => void;
}

const validationSchema = Yup.object({
  supplierName: Yup.string()
    .transform((value) => (value ? value.trim() : ""))
    .required("Supplier is required"),
  tradenameID: Yup.number().required("Trade Name is required"),
});

const SupplierTradeNameForm: FC<SupplierTradeNameFormProps> = ({
  suppliers = [],
  tradenames = [],
  existingAssociations = [],
  selectedSupplier,
  selectedTradename,
  isSaving = false,
  isLoading = false,
  onSave,
  onCancel,
}) => {
  const defaultSupplierName = selectedSupplier?.supplierName ?? "";
  const defaultTradenameID = selectedTradename?.id ?? null;
  const defaultChemID = selectedTradename?.chemID ?? "";
  const [tradeNameInputValue, setTradeNameInputValue] = useState<string>(
    selectedTradename?.tradenameName ?? "",
  );

  useEffect(() => {
    setTradeNameInputValue(selectedTradename?.tradenameName ?? "");
  }, [selectedTradename?.id, selectedTradename?.tradenameName]);

  const initialValues: SupplierTradeNameFormValues = {
    supplierName: defaultSupplierName,
    tradenameID: defaultTradenameID,
    chemID: defaultChemID,
  };

  const hasDuplicateAssociation = (values: SupplierTradeNameFormValues): boolean => {
    return existingAssociations.some(
      (association) =>
        association.supplierName === values.supplierName &&
        association.tradenameID === values.tradenameID,
    );
  };

  const getTradenameByID = (tradenameID: number | null): TradeName | null => {
    return tradenames.find((tradename) => tradename.id === tradenameID) ?? null;
  };

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      validationSchema={validationSchema}
      onSubmit={(values) => onSave?.(values)}
    >
      {({ values, setFieldValue, setFieldTouched, touched, errors }) => {
        const selectedSupplierValue =
          suppliers.find((supplier) => supplier.supplierName === values.supplierName) ?? null;
        const selectedTradenameValue = getTradenameByID(values.tradenameID);
        const chemDisplayName = selectedTradenameValue
          ? `${selectedTradenameValue.chemID ?? ""}${selectedTradenameValue.substanceName ? ` - ${selectedTradenameValue.substanceName}` : ""}`
          : (values.chemID ?? "");
        const isDuplicate = hasDuplicateAssociation(values);
        const hasSupplier = Boolean((values.supplierName ?? "").trim());
        const hasTradename = values.tradenameID !== null;

        return (
          <Form
            style={{
              marginTop: "10px",
              width: "56rem",
              maxWidth: "100%",
              display: "flex",
              flexDirection: "column",
              gap: "30px",
            }}
          >
            <SupplierAutocompleteField
              id="supplier-select"
              value={selectedSupplierValue}
              onChange={(value) => setFieldValue("supplierName", value?.supplierName ?? "")}
              onBlur={() => setFieldTouched("supplierName", true)}
              options={suppliers}
              isLoading={isLoading}
              width="100%"
              name="supplierName"
              error={touched.supplierName && Boolean(errors.supplierName)}
              helperText={touched.supplierName && errors.supplierName}
            />

            <TradeNameAutocompleteField
              id="tradename-select"
              value={selectedTradenameValue}
              inputValue={tradeNameInputValue}
              onChange={(value) => {
                const tradenameID = value?.id ?? null;
                setFieldValue("tradenameID", tradenameID);
                setFieldValue("chemID", value?.chemID ?? "");
                setTradeNameInputValue(value?.tradenameName ?? "");
                if (tradenameID !== null) {
                  setFieldTouched("tradenameID", false);
                }
              }}
              onInputChange={(value, reason) => {
                if (reason === "input" || reason === "clear" || reason === "reset") {
                  setTradeNameInputValue(value ?? "");
                }
              }}
              onBlur={() => setFieldTouched("tradenameID", true)}
              options={tradenames}
              isLoading={isLoading}
              width="100%"
              name="tradenameID"
              error={touched.tradenameID && Boolean(errors.tradenameID)}
              helperText={touched.tradenameID && errors.tradenameID}
            />

            <Tooltip
              title={chemDisplayName}
              placement="top"
              disableHoverListener={!chemDisplayName}
              componentsProps={{
                tooltip: {
                  sx: {
                    fontSize: "0.85rem",
                    maxWidth: 500,
                  },
                },
              }}
            >
                <TextField
                  fullWidth
                  label="Chemical"
                  name="chemID"
                  variant="outlined"
                  size="small"
                  value={chemDisplayName}
                  disabled
                  sx={{
                    "& .MuiInputBase-input": {
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                    },
                  }}
                />
            </Tooltip>

            {isDuplicate && (
              <div style={{ color: "#d32f2f", marginBottom: "16px" }}>
                This Supplier and Trade Name association already exists.
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "center", marginTop: "30px" }}>
              <GlobalButton
                type="submit"
                variant="contained"
                disabled={!hasSupplier || !hasTradename || isSaving || isDuplicate}
              >
                Save
              </GlobalButton>

              <GlobalSecondaryButton
                variant="contained"
                style={{ marginLeft: "40px" }}
                onClick={onCancel}
              >
                Cancel
              </GlobalSecondaryButton>
            </div>
            <ProgressSpinner display={isSaving} />
          </Form>
        );
      }}
    </Formik>
  );
};

export default SupplierTradeNameForm;
